import React, { PropsWithChildren } from 'react'
import clsx from 'clsx'
import { createUseStyles } from '../../lib/styling'
import { Spacing } from '../helperTypes'
import { CommonProps } from '../helperTypes'

type Props = PropsWithChildren<{
  /** The element to use */
  as?: React.ElementType
  /** The className applied to each child */
  childrenClassName?: string
  /** Specifies the padding of the container */
  padding?: Spacing
  /** If true, each child will get wrapped in a div */
  wrapChildren?: boolean
}>

export type FlexLayoutProps = Props & CommonProps

const determineChildType = (parent: React.ElementType) => {
  if (typeof parent !== 'string') return 'div'
  if (parent === 'ul' || parent === 'ol') return 'li'
  if (['span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].indexOf(parent) !== -1) return 'span'
  return 'div'
}

const useStyles = createUseStyles(
  (theme: any) => ({
    root: {
      padding: ({ padding }: FlexLayoutProps) => (padding ? theme.size.spacing.inset[padding] : 0),
    },
    child: {
      listStyleType: 'none',
    },
  }),
  'flex',
)

export const FlexLayout = ({
  className,
  children,
  childrenClassName,
  padding,
  wrapChildren = true,
  as = 'div',
  ...rest
}: FlexLayoutProps) => {
  const classes = useStyles({ padding })
  const Component = as
  const ChildComponent = determineChildType(as)
  const validChildren = React.Children.toArray(children).filter(React.isValidElement)
  return (
    <Component className={clsx(className, classes.root)} {...rest}>
      {validChildren.map((child: React.ReactElement, idx) =>
        wrapChildren ? (
          <ChildComponent
            className={clsx(child.props.className, classes.child, childrenClassName)}
            key={`flex-wrapper-${idx}`}
          >
            {child}
          </ChildComponent>
        ) : (
          React.cloneElement(child, {
            className: clsx(child.props.className, classes.child, childrenClassName),
            style: child.props.style,
          } as React.HTMLProps<HTMLElement>)
        ),
      )}
    </Component>
  )
}

export default FlexLayout
